import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowCounterClockwise, CaretDown, CaretRight, CheckCircle, DownloadSimple, FilePdf, Printer, Receipt } from '@phosphor-icons/react';
import { toast } from 'sonner';
import { useAuth, isAdmin } from '@/auth/AuthContext';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { mktApi, errMsg } from './marketingApi';
import { jd, jdt, yen } from './mktShared';
import { todayJst } from '@/utils/jstDate';

const CLOSING = (d) => (d == null || d === 99 ? '末日' : `${d}日`);
const METHODS = [{ v: 'bank', l: '銀行振込' }, { v: 'cash', l: '現金' }, { v: 'other', l: 'その他' }];

export default function Billing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdmin(user);
  const [month, setMonth] = useState(() => todayJst().slice(0, 7));
  const [data, setData] = useState(null);
  const [unpaid, setUnpaid] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);
  const [payTarget, setPayTarget] = useState(null); // {doc_id, doc_no, customer_name, total}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, u] = await Promise.all([mktApi.billing(month), mktApi.billingUnpaid()]);
      setData(d); setUnpaid(u);
    } catch (e) { toast.error(errMsg(e, '締め請求の取得に失敗しました')); }
    finally { setLoading(false); }
  }, [month]);

  useEffect(() => { load(); }, [load]);

  const issue = async (c) => {
    setBusyId(c.customer_id);
    try {
      const doc = await mktApi.billingIssue({ customer_id: c.customer_id, month });
      toast.success(`月次請求書 ${doc.doc_no} を発行しました`);
      window.open(`/marketing/print/${doc.id}`, '_blank');
      load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusyId(null); }
  };

  const unpay = async (docId) => {
    if (!window.confirm('入金記録を取り消しますか？（領収書も削除されます）')) return;
    try { await mktApi.billingUnpay(docId); toast.success('入金記録を取り消しました'); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const customers = data?.customers || [];
  const grand = customers.reduce((s, c) => s + (c.total || 0), 0);

  return (
    <div className="space-y-4" data-testid="mkt-billing-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-zinc-900">締め請求</h1>
          <p className="text-xs text-zinc-500 mt-0.5">客先ごとの締め日で売上確定済みの受注をまとめ、月次請求書の発行と入金を管理します</p>
        </div>
        <a href={mktApi.billingCsvUrl(month)} download data-testid="mkt-billing-csv-btn">
          <Button variant="outline" size="sm"><DownloadSimple size={14} className="mr-1.5" /> CSVダウンロード</Button>
        </a>
      </div>

      <div className="flex flex-wrap items-center gap-3 rounded-xl border border-zinc-200 bg-white p-3">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-zinc-600">請求月</label>
          <Input type="month" value={month} onChange={(e) => e.target.value && setMonth(e.target.value)}
            className="h-9 w-40" data-testid="mkt-billing-month" />
        </div>
        <div className="ml-auto flex items-center gap-5">
          <div className="text-right">
            <div className="text-[10px] text-zinc-400">未入金（全期間）</div>
            <div className={`text-lg font-bold tabular-nums ${unpaid.length ? 'text-rose-600' : 'text-zinc-900'}`} data-testid="mkt-billing-unpaid-count">{unpaid.length}件</div>
          </div>
          <div className="text-right">
            <div className="text-[10px] text-zinc-400">対象合計（税込）</div>
            <div className="text-lg font-bold tabular-nums text-zinc-900" data-testid="mkt-billing-grand-total">{yen(grand)}</div>
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-zinc-200 bg-zinc-50/70 text-left text-[11px] uppercase tracking-wide text-zinc-400">
                <th className="px-4 py-2.5 font-medium">客先</th>
                <th className="px-3 py-2.5 font-medium">締め日</th>
                <th className="px-3 py-2.5 font-medium">対象期間</th>
                <th className="px-3 py-2.5 font-medium text-right">件数</th>
                <th className="px-3 py-2.5 font-medium text-right">合計(税込)</th>
                <th className="px-3 py-2.5 font-medium">入金</th>
                <th className="px-4 py-2.5 font-medium text-right">請求書</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={7} className="px-4 py-10 text-center text-xs text-zinc-400">読み込み中…</td></tr>}
              {!loading && customers.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-12 text-center text-sm text-zinc-400" data-testid="mkt-billing-empty">
                  この月に売上確定済みの受注はありません
                </td></tr>
              )}
              {!loading && customers.map((c) => (
                <BillingRow key={c.customer_id} c={c} busy={busyId === c.customer_id} admin={admin}
                  expanded={!!expanded[c.customer_id]}
                  onToggle={() => setExpanded((p) => ({ ...p, [c.customer_id]: !p[c.customer_id] }))}
                  onIssue={() => issue(c)} onOpenOrder={(oid) => navigate(`/marketing/orders/${oid}`)}
                  onPay={() => setPayTarget({ doc_id: c.last_issued.doc_id, doc_no: c.last_issued.doc_no, customer_name: c.customer_name, total: c.total })}
                  onUnpay={() => unpay(c.last_issued.doc_id)} />
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {unpaid.length > 0 && (
        <div className="rounded-xl border border-rose-200 bg-white overflow-hidden" data-testid="mkt-billing-unpaid-section">
          <div className="border-b border-rose-100 bg-rose-50/60 px-4 py-2.5">
            <h2 className="text-sm font-semibold text-rose-700">未入金の請求書（全期間）</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <tbody>
                {unpaid.map((u) => (
                  <tr key={u.doc_id} className="border-b border-zinc-50 last:border-0" data-testid={`mkt-unpaid-row-${u.doc_id}`}>
                    <td className="px-4 py-2.5 font-mono text-xs font-semibold text-fuchsia-700">{u.doc_no}</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-700"><span data-no-translate>{u.customer_name}</span></td>
                    <td className="px-3 py-2.5 text-xs tabular-nums text-zinc-500">{u.month} 締め分</td>
                    <td className="px-3 py-2.5 text-xs text-zinc-400">発行 {jd(u.issued_at)}</td>
                    <td className="px-3 py-2.5 text-right text-xs font-bold tabular-nums text-zinc-900">{yen(u.total)}</td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <Button variant="outline" size="sm" className="h-7 mr-1.5"
                        onClick={() => window.open(`/marketing/print/${u.doc_id}`, '_blank')}
                        data-testid={`mkt-unpaid-print-${u.doc_id}`}>
                        <Printer size={13} className="mr-1" /> 印刷
                      </Button>
                      <Button size="sm" className="h-7 bg-teal-700 hover:bg-teal-800"
                        onClick={() => setPayTarget({ doc_id: u.doc_id, doc_no: u.doc_no, customer_name: u.customer_name, total: u.total })}
                        data-testid={`mkt-unpaid-pay-${u.doc_id}`}>
                        <CheckCircle size={13} className="mr-1" /> 入金記録
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <PaymentDialog target={payTarget} onClose={() => setPayTarget(null)} onDone={load} />
    </div>
  );
}

function BillingRow({ c, busy, admin, expanded, onToggle, onIssue, onOpenOrder, onPay, onUnpay }) {
  const li = c.last_issued;
  return (
    <>
      <tr className="border-b border-zinc-50 hover:bg-fuchsia-50/30 transition-colors" data-testid={`mkt-billing-row-${c.customer_id}`}>
        <td className="px-4 py-3">
          <button type="button" onClick={onToggle} className="flex items-center gap-1.5 text-left" data-testid={`mkt-billing-expand-${c.customer_id}`}>
            {expanded ? <CaretDown size={13} className="text-zinc-400 shrink-0" /> : <CaretRight size={13} className="text-zinc-400 shrink-0" />}
            <span>
              <span className="font-medium text-zinc-900"><span data-no-translate>{c.customer_name}</span></span>
              {c.code && <span className="ml-2 font-mono text-[10px] text-zinc-400">{c.code}</span>}
            </span>
          </button>
        </td>
        <td className="px-3 py-3 text-xs text-zinc-600">{CLOSING(c.closing_day)}</td>
        <td className="px-3 py-3 text-xs tabular-nums text-zinc-600">{jd(c.period_from)} 〜 {jd(c.period_to)}</td>
        <td className="px-3 py-3 text-right text-xs tabular-nums text-zinc-600">{c.orders.length}件</td>
        <td className="px-3 py-3 text-right text-xs font-bold tabular-nums text-zinc-900">{yen(c.total)}</td>
        <td className="px-3 py-3 whitespace-nowrap" data-testid={`mkt-billing-payment-${c.customer_id}`}>
          {!li ? <span className="text-[11px] text-zinc-300">—</span>
            : li.payment ? (
              <span className="inline-flex items-center gap-1">
                <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">入金済 {jd(li.payment.paid_at)}</span>
                {li.receipt_doc_id && (
                  <button type="button" onClick={() => window.open(`/marketing/print/${li.receipt_doc_id}`, '_blank')}
                    className="text-zinc-400 hover:text-emerald-600 transition-colors" title="領収書を印刷"
                    data-testid={`mkt-billing-receipt-${c.customer_id}`}><Receipt size={15} /></button>
                )}
                {admin && (
                  <button type="button" onClick={onUnpay} className="text-zinc-300 hover:text-red-500 transition-colors" title="入金取消（管理者）"
                    data-testid={`mkt-billing-unpay-${c.customer_id}`}><ArrowCounterClockwise size={13} /></button>
                )}
              </span>
            ) : (
              <Button size="sm" variant="outline" className="h-7 text-emerald-700 border-emerald-200 hover:bg-emerald-50"
                onClick={onPay} data-testid={`mkt-billing-pay-${c.customer_id}`}>
                <CheckCircle size={13} className="mr-1" /> 入金記録
              </Button>
            )}
        </td>
        <td className="px-4 py-3 text-right whitespace-nowrap">
          {li && (
            <Button variant="outline" size="sm" className="h-7 mr-1.5"
              onClick={() => window.open(`/marketing/print/${li.doc_id}`, '_blank')}
              title={`${li.doc_no}（${jdt(li.issued_at)}）`}
              data-testid={`mkt-billing-reprint-${c.customer_id}`}>
              <Printer size={13} className="mr-1" /> {li.doc_no}
            </Button>
          )}
          {!(li && li.payment) && (
            <Button size="sm" disabled={busy} onClick={onIssue}
              className="h-7 bg-teal-700 hover:bg-teal-800" data-testid={`mkt-billing-issue-${c.customer_id}`}>
              <FilePdf size={13} className="mr-1" /> {li ? '再発行' : '請求書発行'}
            </Button>
          )}
        </td>
      </tr>
      {expanded && (
        <tr className="border-b border-zinc-50 bg-zinc-50/50">
          <td colSpan={7} className="px-6 py-2">
            <table className="w-full text-xs">
              <tbody>
                {c.orders.map((o) => (
                  <tr key={o.order_id} className="border-b border-zinc-200 last:border-0" data-testid={`mkt-billing-order-${o.order_id}`}>
                    <td className="py-1.5 pr-3 tabular-nums text-zinc-500 w-24">{jd(o.completed_at)}</td>
                    <td className="py-1.5 pr-3 w-32">
                      <button type="button" onClick={() => onOpenOrder(o.order_id)}
                        className="font-mono font-semibold text-fuchsia-700 hover:underline">{o.order_no}</button>
                    </td>
                    <td className="py-1.5 pr-3 text-zinc-700">{o.title || o.summary}</td>
                    <td className="py-1.5 pr-3 text-zinc-400">{o.technoa_no ? `Technoa: ${o.technoa_no}` : ''}</td>
                    <td className="py-1.5 text-right font-semibold tabular-nums text-zinc-700 w-28">{yen(o.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  );
}

function PaymentDialog({ target, onClose, onDone }) {
  const [f, setF] = useState({ paid_at: '', method: 'bank', note: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (target) setF({ paid_at: todayJst(), method: 'bank', note: '' });
  }, [target]);

  const save = async () => {
    if (!f.paid_at) { toast.error('入金日を入力してください'); return; }
    setSaving(true);
    try {
      const res = await mktApi.billingPay({ doc_id: target.doc_id, paid_at: f.paid_at, method: f.method, note: f.note || null });
      toast.success(`入金を記録しました（領収書 ${res.receipt.doc_no} 発行）`);
      window.open(`/marketing/print/${res.receipt.id}`, '_blank');
      onClose(); onDone();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={!!target} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-sm" data-testid="mkt-payment-dialog">
        <DialogHeader><DialogTitle>入金記録 — {target?.doc_no}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="rounded-lg bg-emerald-50 border border-emerald-100 px-3 py-2 text-[11px] text-emerald-700">
            {target?.customer_name} ／ {yen(target?.total)}（税込）。記録すると領収書が自動発行されます。
          </p>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-medium text-zinc-600">入金日 <span className="text-red-500">*</span></label>
              <Input type="date" value={f.paid_at} onChange={(e) => setF((p) => ({ ...p, paid_at: e.target.value }))}
                className="mt-1" data-testid="mkt-payment-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-zinc-600">支払方法</label>
              <select value={f.method} onChange={(e) => setF((p) => ({ ...p, method: e.target.value }))}
                className="mt-1 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid="mkt-payment-method">
                {METHODS.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-zinc-600">備考</label>
            <Textarea value={f.note} onChange={(e) => setF((p) => ({ ...p, note: e.target.value }))} rows={2}
              className="mt-1" data-testid="mkt-payment-note" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={onClose}>キャンセル</Button>
            <Button disabled={saving} onClick={save} className="bg-teal-700 hover:bg-teal-800" data-testid="mkt-payment-save-btn">
              <CheckCircle size={14} className="mr-1" /> {saving ? '保存中…' : '入金を記録'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
